/**
 * Memory Metrics — visão dedicada para health da camada de memória.
 *
 * KPIs (últimos 7d):
 *   - Hit rate         = proceed / (proceed + escalate_with_context)  em memory_retrieval_trace
 *   - Latência p50/p95 = quantis de latency_ms em memory_retrieval_trace
 *   - Tamanho consol.  = count(*) consolidated_memories WHERE active
 *
 * Tabela "Grupos que precisam de backfill":
 *   - Heurística: grupos com mensagens recentes (7d) mas baixa cobertura
 *     de consolidated_memories ativas OU último job em status 'failed' / 'pending'.
 *
 * Sem novas tabelas — leituras agregadas via tabelas já existentes.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Activity, Database, Gauge, Timer, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";

// ── helpers ────────────────────────────────────────────────────────────
function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

// ── component ──────────────────────────────────────────────────────────
export default function MemoryMetricsPage() {
  // Retrieval traces (últimos 7d) — base para hit rate e latência.
  const { data: traces = [], isLoading: tracesLoading } = useQuery({
    queryKey: ["mm-traces-7d"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const { data } = await supabase
        .from("memory_retrieval_trace")
        .select("action, latency_ms, created_at, group_id")
        .gte("created_at", since)
        .limit(5000);
      return data ?? [];
    },
  });

  // Consolidated memories (snapshot agregado).
  const { data: consolidated = [], isLoading: consolLoading } = useQuery({
    queryKey: ["mm-consolidated-snapshot"],
    queryFn: async () => {
      const { data } = await supabase
        .from("consolidated_memories")
        .select("group_id, active, memory_layer, memory_type")
        .eq("active", true)
        .limit(10000);
      return data ?? [];
    },
  });

  // Mensagens recentes por grupo (7d) para identificar candidatos a backfill.
  const { data: recentMsgs = [], isLoading: msgsLoading } = useQuery({
    queryKey: ["mm-recent-msgs-7d"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const { data } = await supabase
        .from("syra_messages")
        .select("group_id")
        .gte("created_at", since)
        .not("group_id", "is", null)
        .limit(20000);
      return data ?? [];
    },
  });

  // Backfill jobs (último por grupo).
  const { data: backfillJobs = [] } = useQuery({
    queryKey: ["mm-backfill-jobs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("person_memory_backfill_jobs")
        .select("group_id, status, messages_processed, errors_count, last_error, finished_at, updated_at")
        .order("updated_at", { ascending: false })
        .limit(2000);
      return data ?? [];
    },
  });

  // Grupos (nome).
  const { data: groups = [] } = useQuery({
    queryKey: ["mm-groups"],
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_groups")
        .select("id, name")
        .order("name", { ascending: true })
        .limit(500);
      return data ?? [];
    },
  });

  // ── KPIs agregados ──────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = traces.length;
    const hits = traces.filter((t: any) => t.action === "proceed").length;
    const escalations = traces.filter((t: any) => t.action === "escalate_with_context").length;
    const denom = hits + escalations;
    const hitRate = denom > 0 ? hits / denom : 0;
    const lats = traces
      .map((t: any) => Number(t.latency_ms))
      .filter((n: number) => Number.isFinite(n) && n >= 0)
      .sort((a: number, b: number) => a - b);
    return {
      total,
      hitRate,
      p50: Math.round(quantile(lats, 0.5)),
      p95: Math.round(quantile(lats, 0.95)),
      consolidatedTotal: consolidated.length,
    };
  }, [traces, consolidated]);

  // ── Hit rate por dia ────────────────────────────────────────────────
  const hitRateByDay = useMemo(() => {
    const buckets: Record<string, { day: string; hits: number; escal: number }> = {};
    for (const t of traces as any[]) {
      const d = new Date(t.created_at).toISOString().slice(0, 10);
      buckets[d] ??= { day: d, hits: 0, escal: 0 };
      if (t.action === "proceed") buckets[d].hits++;
      else if (t.action === "escalate_with_context") buckets[d].escal++;
    }
    return Object.values(buckets)
      .map((b) => ({
        day: b.day.slice(5),
        hit_rate: (b.hits + b.escal) > 0 ? +(100 * b.hits / (b.hits + b.escal)).toFixed(1) : 0,
        volume: b.hits + b.escal,
      }))
      .sort((a, b) => (a.day < b.day ? -1 : 1));
  }, [traces]);

  // ── Latência: histograma ────────────────────────────────────────────
  const latencyHist = useMemo(() => {
    const bins = [
      { label: "0-50", min: 0, max: 50, count: 0 },
      { label: "50-100", min: 50, max: 100, count: 0 },
      { label: "100-250", min: 100, max: 250, count: 0 },
      { label: "250-500", min: 250, max: 500, count: 0 },
      { label: "500-1k", min: 500, max: 1000, count: 0 },
      { label: "1k-2k", min: 1000, max: 2000, count: 0 },
      { label: "2k+", min: 2000, max: Infinity, count: 0 },
    ];
    for (const t of traces as any[]) {
      const v = Number(t.latency_ms);
      if (!Number.isFinite(v)) continue;
      for (const b of bins) {
        if (v >= b.min && v < b.max) { b.count++; break; }
      }
    }
    return bins;
  }, [traces]);

  // ── Backfill candidates ─────────────────────────────────────────────
  const groupNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const g of groups as any[]) m[g.id] = g.name;
    return m;
  }, [groups]);

  const backfillCandidates = useMemo(() => {
    const msgsByGroup: Record<string, number> = {};
    for (const m of recentMsgs as any[]) {
      if (!m.group_id) continue;
      msgsByGroup[m.group_id] = (msgsByGroup[m.group_id] ?? 0) + 1;
    }
    const consolByGroup: Record<string, number> = {};
    for (const c of consolidated as any[]) {
      if (!c.group_id) continue;
      consolByGroup[c.group_id] = (consolByGroup[c.group_id] ?? 0) + 1;
    }
    const lastJobByGroup: Record<string, any> = {};
    for (const j of backfillJobs as any[]) {
      if (!j.group_id) continue;
      if (!lastJobByGroup[j.group_id]) lastJobByGroup[j.group_id] = j;
    }
    const candidates = Object.entries(msgsByGroup).map(([gid, msgs]) => {
      const consolCount = consolByGroup[gid] ?? 0;
      const ratio = consolCount / Math.max(msgs, 1);
      const lastJob = lastJobByGroup[gid];
      const reasons: string[] = [];
      if (consolCount === 0) reasons.push("sem memória consolidada");
      else if (ratio < 0.01) reasons.push(`baixa cobertura (${(ratio * 100).toFixed(2)}%)`);
      if (lastJob?.status === "failed") reasons.push("último job falhou");
      else if (lastJob?.status === "pending") reasons.push("job pendente");
      else if (!lastJob) reasons.push("nunca rodou");
      const severity =
        consolCount === 0 || lastJob?.status === "failed"
          ? "high"
          : ratio < 0.01 || !lastJob
            ? "medium"
            : null;
      return {
        group_id: gid,
        group_name: groupNameById[gid] ?? gid.slice(0, 8),
        msgs_7d: msgs,
        consol_count: consolCount,
        ratio,
        last_job_status: lastJob?.status ?? null,
        last_job_at: lastJob?.updated_at ?? null,
        last_error: lastJob?.last_error ?? null,
        reasons,
        severity,
      };
    });
    return candidates
      .filter((c) => c.severity !== null)
      .sort((a, b) => {
        if (a.severity !== b.severity) return a.severity === "high" ? -1 : 1;
        return b.msgs_7d - a.msgs_7d;
      })
      .slice(0, 50);
  }, [recentMsgs, consolidated, backfillJobs, groupNameById]);

  // ── Layer breakdown (consolidated) ───────────────────────────────────
  const layerBreakdown = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of consolidated as any[]) {
      const k = c.memory_layer ?? "unknown";
      m[k] = (m[k] ?? 0) + 1;
    }
    return Object.entries(m).map(([layer, count]) => ({ layer, count }));
  }, [consolidated]);

  const isLoading = tracesLoading || consolLoading || msgsLoading;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Métricas de Memória</h1>
        <p className="text-sm text-muted-foreground">
          Hit rate, latência, tamanho consolidado e grupos que precisam de backfill.
          Janela: últimos 7 dias.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<Gauge className="w-4 h-4" />}
          label="Hit rate (proceed)"
          value={fmtPct(kpis.hitRate)}
          hint={`${kpis.total} consultas em 7d`}
          tone={kpis.hitRate >= 0.7 ? "good" : kpis.hitRate >= 0.4 ? "warn" : "bad"}
        />
        <KpiCard
          icon={<Timer className="w-4 h-4" />}
          label="Latência p50"
          value={`${kpis.p50} ms`}
          hint="mediana de retrieval"
          tone={kpis.p50 <= 300 ? "good" : kpis.p50 <= 800 ? "warn" : "bad"}
        />
        <KpiCard
          icon={<Activity className="w-4 h-4" />}
          label="Latência p95"
          value={`${kpis.p95} ms`}
          hint="cauda de retrieval"
          tone={kpis.p95 <= 800 ? "good" : kpis.p95 <= 2000 ? "warn" : "bad"}
        />
        <KpiCard
          icon={<Database className="w-4 h-4" />}
          label="Memórias consolidadas"
          value={kpis.consolidatedTotal.toLocaleString()}
          hint="ativas em todos os grupos"
          tone="neutral"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h2 className="text-sm font-semibold mb-2">Hit rate por dia</h2>
          <p className="text-xs text-muted-foreground mb-3">
            % de retrievals com action=<code>proceed</code> (sem escalation).
          </p>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={hitRateByDay}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="hit_rate"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  name="Hit rate %"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-semibold mb-2">Distribuição de latência</h2>
          <p className="text-xs text-muted-foreground mb-3">
            Buckets de <code>latency_ms</code> em <code>memory_retrieval_trace</code>.
          </p>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={latencyHist}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" name="consultas" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Layer breakdown */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold mb-3">Tamanho consolidado por camada</h2>
        {layerBreakdown.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nenhuma memória ativa.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {layerBreakdown.map((l) => (
              <div key={l.layer} className="border rounded-md p-3">
                <div className="text-xs text-muted-foreground">{l.layer}</div>
                <div className="text-lg font-semibold">{l.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Backfill candidates */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Grupos que precisam de backfill
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Heurística: tem mensagens nos últimos 7d mas pouca cobertura de
            <code className="mx-1">consolidated_memories</code>, ou último job
            de backfill em <code>failed</code>/<code>pending</code>.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Grupo</TableHead>
              <TableHead className="text-right">Msgs 7d</TableHead>
              <TableHead className="text-right">Consol.</TableHead>
              <TableHead className="text-right">Cobertura</TableHead>
              <TableHead>Último job</TableHead>
              <TableHead>Motivos</TableHead>
              <TableHead>Severidade</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && backfillCandidates.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  Nenhum grupo precisa de backfill 🎉
                </TableCell>
              </TableRow>
            )}
            {backfillCandidates.map((c) => (
              <TableRow key={c.group_id}>
                <TableCell className="text-sm font-medium">
                  {c.group_name}
                  <div className="text-xs text-muted-foreground font-mono">
                    {c.group_id.slice(0, 8)}
                  </div>
                </TableCell>
                <TableCell className="text-right text-xs font-mono">{c.msgs_7d}</TableCell>
                <TableCell className="text-right text-xs font-mono">{c.consol_count}</TableCell>
                <TableCell className="text-right text-xs font-mono">
                  {(c.ratio * 100).toFixed(2)}%
                </TableCell>
                <TableCell className="text-xs">
                  {c.last_job_status ? (
                    <>
                      <Badge variant="outline">{c.last_job_status}</Badge>
                      {c.last_job_at && (
                        <div className="text-muted-foreground mt-0.5">
                          {new Date(c.last_job_at).toLocaleString()}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">nunca rodou</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-col gap-0.5">
                    {c.reasons.map((r, i) => (
                      <span key={i}>• {r}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      c.severity === "high"
                        ? "bg-destructive/10 text-destructive border-destructive/30"
                        : "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
                    }
                  >
                    {c.severity}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ── small KPI card ────────────────────────────────────────────────────
function KpiCard({
  icon, label, value, hint, tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone: "good" | "warn" | "bad" | "neutral";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "bad"
          ? "text-destructive"
          : "text-foreground";
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}
